/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect } from "react";
import { useSelector } from "react-redux";
import { RootState } from "@store/store";
import type { StringSchema } from "yup";
import type {
  SolicitudCreditoFieldName,
  SolicitudCreditoStepsNames,
  TabsConfigResponseFormConfigField,
  TabsConfigResponseFormConfigSolicitudCredito,
  TabsConfigResponseFormConfigStep,
} from "@interfaces/response";
import type { DynamicQuestionCatalogItem } from "@interfaces/catalogs";
import { useLazyGetDynamicQuestionConfigQuery, useLazyGetTabsConfigQuery } from "@api/catalogs";
import { BOFU_TABS_CONFIG, BOFU_TABS_FORM_CODES } from "@helpers/constantes";

type TaxSystemTypeCode = "PM" | "PFAE" | "PF";

const enabledField = (required: boolean): TabsConfigResponseFormConfigField => ({
  enabled: true,
  required,
});

const domicilioField: TabsConfigResponseFormConfigField = {
  enabled: true,
};

const DEFAULT_STEP_1_PM: TabsConfigResponseFormConfigStep = {
  code: "EMPRESA_Y_DOMICILIO",
  fields: {
    cell_phone_number: enabledField(true),
    giro: enabledField(true),
    descripcion: enabledField(true),
    domicilio_operativo: domicilioField,
  },
};

const DEFAULT_STEP_1_PF: TabsConfigResponseFormConfigStep = {
  code: "EMPRESA_Y_DOMICILIO",
  fields: {
    phone: enabledField(true),
    email: enabledField(true),
    ocupacion: enabledField(true),
    domicilio_particular: domicilioField,
  },
};

const DEFAULT_STEP_1_PFAE: TabsConfigResponseFormConfigStep = {
  code: "EMPRESA_Y_DOMICILIO",
  fields: {
    curp: enabledField(true),
    ocupacion: enabledField(true),
    estado_civil_id: enabledField(true),
    matrimonial_regimes_id: enabledField(true),
    conyuge_id: enabledField(true),
    conyuge_name: enabledField(true),
    conyuge_last_name: enabledField(true),
    conyuge_last_name_2: enabledField(false),
    domicilio_operativo: domicilioField,
    domicilio_particular: domicilioField,
  },
};

// Mock temporal: mientras el API no entregue config.step_2.fields, esto simula lo que
// llegaría por configuración. El día que el backend lo entregue, se puede borrar este mock:
// hasStepFields ya prioriza lo que venga del API sobre estos defaults.
// Nota: no incluye "dynamic_questions" — si el API no manda config para el step, no se
// muestra ninguna pregunta dinámica (no hay ids reales que inventar aquí).
const STEP_2_FIXED_FIELDS: Record<string, TabsConfigResponseFormConfigField> = {
  paymentMethods: enabledField(true),
  advancePayments: enabledField(true),
  resourcesTypes: enabledField(true),
  otherPaymentMethod: enabledField(true),
};

const DEFAULT_STEP_2_PM: TabsConfigResponseFormConfigStep = {
  code: "INFORMACION_TRANSACCIONAL",
  fields: STEP_2_FIXED_FIELDS,
};

const DEFAULT_STEP_2_PF: TabsConfigResponseFormConfigStep = {
  code: "INFORMACION_TRANSACCIONAL",
  fields: STEP_2_FIXED_FIELDS,
};

const DEFAULT_STEP_2_PFAE: TabsConfigResponseFormConfigStep = {
  code: "INFORMACION_TRANSACCIONAL",
  fields: STEP_2_FIXED_FIELDS,
};

// Mock temporal (PM, PF y PFAE): mientras no exista dónde configurar "PEPS" en backend, esta
// config simula lo que llegaría por configuración para mostrar u ocultar cada campo.
const DEFAULT_STEP_3_PM: TabsConfigResponseFormConfigStep = {
  code: "PEPS",
  fields: {
    socioFuncionario: enabledField(true),
    conyuge: enabledField(true),
  },
};

const DEFAULT_STEPS_BY_TAX_SYSTEM: Record<
  TaxSystemTypeCode,
  Partial<Record<SolicitudCreditoStepsNames, TabsConfigResponseFormConfigStep>>
> = {
  PM: {
    step_1: DEFAULT_STEP_1_PM,
    step_2: DEFAULT_STEP_2_PM,
    step_3: DEFAULT_STEP_3_PM,
  },
  PF: {
    step_1: DEFAULT_STEP_1_PF,
    step_2: DEFAULT_STEP_2_PF,
    step_3: DEFAULT_STEP_3_PM,
  },
  PFAE: {
    step_1: DEFAULT_STEP_1_PFAE,
    step_2: DEFAULT_STEP_2_PFAE,
    step_3: DEFAULT_STEP_3_PM,
  },
};

const getDefaultStepConfig = (
  taxSystemType: TaxSystemTypeCode,
  step: SolicitudCreditoStepsNames,
): TabsConfigResponseFormConfigStep | undefined => DEFAULT_STEPS_BY_TAX_SYSTEM[taxSystemType][step];

const getSolicitudCreditoStepConfig = (
  config: TabsConfigResponseFormConfigSolicitudCredito | undefined,
  step: SolicitudCreditoStepsNames,
): TabsConfigResponseFormConfigStep | undefined => config?.[step];

const hasStepFields = (stepConfig: TabsConfigResponseFormConfigStep | undefined): boolean =>
  Boolean(stepConfig?.fields && Object.keys(stepConfig.fields).length > 0);

const isStepFieldVisible = (
  stepConfig: TabsConfigResponseFormConfigStep | undefined,
  field: SolicitudCreditoFieldName,
): boolean => {
  if (!hasStepFields(stepConfig)) return true;
  return stepConfig?.fields?.[field]?.enabled ?? false;
};

const isStepFieldRequired = (
  stepConfig: TabsConfigResponseFormConfigStep | undefined,
  field: SolicitudCreditoFieldName,
  fallback: boolean,
): boolean => {
  if (!isStepFieldVisible(stepConfig, field)) return false;
  const fieldConfig = stepConfig?.fields?.[field];
  if (fieldConfig?.required === undefined) return fallback;
  return fieldConfig.required;
};

/** Tab donde vive el form `SOLICITUD_CREDITO`: `TBEPF` en PF, `TBEMP` en PM/PFAE. */
const getSolicitudCreditoTabCode = (taxSystemType: TaxSystemTypeCode): string =>
  taxSystemType === "PF" ? BOFU_TABS_CONFIG.tab_representante_pf.code : BOFU_TABS_CONFIG.tab_empresa.code;

const extractSolicitudCreditoConfig = (
  tabsConfig: Array<{ tabs_bofu: { code: string }; forms?: Array<{ code: string; config?: unknown }> }> | undefined,
  tabCode: string,
): TabsConfigResponseFormConfigSolicitudCredito | undefined => {
  const tab = tabsConfig?.find((item) => item.tabs_bofu.code === tabCode);
  const form = tab?.forms?.find((f) => f.code === BOFU_TABS_FORM_CODES.solicitud_credito);
  return (form?.config as TabsConfigResponseFormConfigSolicitudCredito | undefined) ?? undefined;
};

/**
 * Controla visibilidad / obligatoriedad de campos en solicitud de crédito.
 *
 * Consulta `tabs_bofu_config` al montar (`TBEMP` en PM/PFAE, `TBEPF` en PF).
 * Si el step trae `fields` del API, los usa; si no (vacío / error), cae a defaults por `tax_system_type`.
 *
 * Para preguntas dinámicas, las pide solo cuando el step las declara.
 */
export const useSolicitudCreditoForm = (step: SolicitudCreditoStepsNames) => {
  const taxcode = useSelector((state: RootState) => state.register.application.type);
  const taxSystemType = useSelector((state: RootState) => state.register.application.tax_system_type);
  const tabCode = getSolicitudCreditoTabCode(taxSystemType);

  const [getTabsConfig, { data: tabsConfig, isFetching, isUninitialized, isError }] = useLazyGetTabsConfigQuery();
  const [getDynamicQuestionConfig, { data: dynamicQuestionsCatalog }] = useLazyGetDynamicQuestionConfigQuery();

  useEffect(() => {
    if (!taxcode) return;
    getTabsConfig({ taxcode, code: tabCode });
  }, [taxcode, tabCode]);

  const apiConfig = extractSolicitudCreditoConfig(tabsConfig, tabCode);
  const apiStepConfig = getSolicitudCreditoStepConfig(apiConfig, step);
  const stepConfig = hasStepFields(apiStepConfig) ? apiStepConfig : getDefaultStepConfig(taxSystemType, step);

  const isVisible = (field: SolicitudCreditoFieldName) => isStepFieldVisible(stepConfig, field);

  const isRequired = (field: SolicitudCreditoFieldName, fallback: boolean) =>
    isStepFieldRequired(stepConfig, field, fallback);

  const requiredField = <T extends StringSchema>(
    schema: T,
    field: SolicitudCreditoFieldName,
    fallback: boolean,
    message = "Requerido",
  ) => (isRequired(field, fallback) ? schema.required(message) : schema.notRequired());

  const dynamicQuestionIds = stepConfig?.dynamic_questions ?? [];

  useEffect(() => {
    if (dynamicQuestionIds.length > 0) {
      getDynamicQuestionConfig();
    }
  }, [dynamicQuestionIds.length]);

  const questions: DynamicQuestionCatalogItem[] =
    dynamicQuestionIds.length > 0 && dynamicQuestionsCatalog
      ? dynamicQuestionIds
          .map((id) => dynamicQuestionsCatalog.find((item) => item.id === id))
          .filter((item): item is DynamicQuestionCatalogItem => Boolean(item?.active))
      : [];

  // Solo la primera resolución (éxito o error). No vuelve a bloquear en refetch con cache.
  const isConfigLoading = Boolean(taxcode) && !isError && tabsConfig === undefined && (isUninitialized || isFetching);

  return {
    isVisible,
    isRequired,
    requiredField,
    stepConfig,
    questions,
    isConfigLoading,
  };
};

export default useSolicitudCreditoForm;
